import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { layout, tokens, visuallyHidden } from '@alola/ui';
import { useLocale } from '../locale';

/**
 * What the first load and a refresh show while the session is checked (UX-034): the outline of the
 * shell — sidebar at the inline start, top bar, a header and cards — instead of one card alone on an
 * empty page. The words "loading" are announced to assistive technology; the shapes are decorative.
 */
export function ShellSkeleton() {
  const { t } = useLocale();
  return (
    <Box
      aria-busy="true"
      data-testid="shell-skeleton"
      sx={{ minHeight: '100vh', display: 'flex', backgroundColor: 'background.default' }}
    >
      <Box role="status" sx={visuallyHidden}>
        {t('states.loadingTitle')}
      </Box>
      <Box
        aria-hidden
        sx={{
          display: { xs: 'none', md: 'block' },
          inlineSize: layout.sidebarWidthPx,
          flexShrink: 0,
          borderInlineEnd: 1,
          borderColor: tokens.borderSoft,
          backgroundColor: 'background.paper',
          padding: 2,
        }}
      >
        <Skeleton variant="rounded" height={40} sx={{ marginBlockEnd: 3 }} />
        {Array.from({ length: 9 }, (_, index) => (
          <Skeleton key={index} variant="text" height={32} sx={{ marginBlockEnd: 0.5 }} />
        ))}
      </Box>
      <Box aria-hidden sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box
          sx={{
            blockSize: layout.topBarHeightPx,
            borderBlockEnd: 1,
            borderColor: tokens.borderSoft,
            backgroundColor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            paddingInline: 2,
            gap: 2,
          }}
        >
          <Skeleton variant="rounded" width={280} height={36} sx={{ maxWidth: '60%' }} />
          <Box sx={{ flexGrow: 1 }} />
          <Skeleton variant="circular" width={36} height={36} />
        </Box>
        <Box sx={{ padding: { xs: 2, md: 3 } }}>
          <Skeleton variant="text" width={240} height={40} sx={{ maxWidth: '70%' }} />
          <Skeleton
            variant="text"
            width={360}
            height={24}
            sx={{ maxWidth: '90%', marginBlockEnd: 3 }}
          />
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))',
            }}
          >
            {Array.from({ length: 4 }, (_, index) => (
              <Skeleton key={index} variant="rounded" height={110} />
            ))}
          </Box>
          <Skeleton variant="rounded" height={240} sx={{ marginBlockStart: 3 }} />
        </Box>
      </Box>
    </Box>
  );
}
